/* LandingV3.jsx — Funnel-versie: focus op GRATIS zonnecheck + offerte-vergelijker
   Route: /
   - 100% i18n via `landing_v3.*` keys (7 talen)
   - Bevat nieuwe `CompareCTA` sectie met link naar /offertes-vergelijken
*/

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  ArrowRight, Check, Star, ChevronDown, ShieldCheck, Sparkles,
  Sun, TrendingDown, Home as HomeIcon, Banknote, Map, Loader2, Play,
  FileText, BarChart3, Facebook, Instagram,
} from "lucide-react";
import { apiClient } from "../lib/api";
import { getUtm } from "../lib/utm";
import { trackLead } from "../lib/fbpixel";
import LanguageSwitcher from "../components/LanguageSwitcher";

export default function LandingV3() {
  const [regions, setRegions] = useState([]);
  useEffect(() => {
    apiClient.get("/regions").then((r) => setRegions(r.data.regions)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white text-[#003049] antialiased font-sans">
      <HeaderV3 />
      <Hero />
      <WhatYouGet />
      <HowItWorks />
      <CompareCTA />
      <Reviews />
      <SunCheckForm regions={regions} />
      <FaqMini />
      <FooterV3 />
    </div>
  );
}

/* ─── HEADER ─── */
function HeaderV3() {
  const { t } = useTranslation();
  return (
    <header className="bg-white border-b border-[#E5E9EE] sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="v3-brand" className="flex items-center">
          <img src="/img/doctorsol-logo.svg" alt="DoctorSol" className="h-12 w-auto" draggable={false} />
        </Link>
        <nav className="hidden lg:flex items-center gap-7 text-sm text-[#003049] font-medium">
          <a href="#wat-krijg-je" className="hover:text-[#FCBF49]">{t("landing_v3.header.nav_what")}</a>
          <a href="#hoe-werkt-het" className="hover:text-[#FCBF49]">{t("landing_v3.header.nav_how")}</a>
          <Link to="/offertes-vergelijken" className="hover:text-[#FCBF49]" data-testid="v3-header-compare">{t("landing_v3.header.nav_compare")}</Link>
          <Link to="/kennisbank" className="hover:text-[#FCBF49]">{t("landing_v3.header.nav_knowledge")}</Link>
          <Link to="/over-ons" className="hover:text-[#FCBF49]">{t("landing_v3.header.nav_about")}</Link>
        </nav>
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <a href="#zonnecheck" data-testid="v3-header-cta"
            className="bg-[#FCBF49] hover:bg-[#E5AB2F] text-[#003049] font-semibold rounded-md px-5 py-2.5 text-sm inline-flex items-center gap-2">
            {t("landing_v3.header.cta")} <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </header>
  );
}

/* ─── HERO ─── */
function Hero() {
  const { t } = useTranslation();
  return (
    <section className="relative overflow-hidden bg-[#F4F7FA]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <div className="inline-flex items-center gap-2 bg-white border border-[#1F7A4B]/20 rounded-full px-4 py-1.5 mb-6">
            <span className="w-2 h-2 rounded-full bg-[#1F7A4B] animate-pulse" />
            <span className="text-xs font-semibold text-[#1F7A4B] uppercase tracking-wider">{t("landing_v3.hero.badge")}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-[58px] leading-[1.05] font-bold text-[#003049] tracking-tight">
            {t("landing_v3.hero.title_1")}<br />{t("landing_v3.hero.title_2")}<br />
            <span className="text-[#FCBF49]">{t("landing_v3.hero.title_3")}</span>
          </h1>
          <p className="mt-7 text-lg text-[#52606E] max-w-lg leading-relaxed">{t("landing_v3.hero.subtitle")}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a href="#zonnecheck" data-testid="v3-hero-cta-primary"
              className="bg-[#FCBF49] hover:bg-[#E5AB2F] text-[#003049] font-semibold rounded-md px-7 py-4 inline-flex items-center justify-center gap-2">
              {t("landing_v3.hero.cta_primary")} <ArrowRight className="w-4 h-4" />
            </a>
            <a href="#wat-krijg-je" data-testid="v3-hero-cta-secondary"
              className="border border-[#003049]/20 hover:border-[#003049]/50 text-[#003049] rounded-md px-7 py-4 inline-flex items-center justify-center gap-2 bg-white">
              <Play className="w-4 h-4" /> {t("landing_v3.hero.cta_secondary")}
            </a>
          </div>
          <div className="mt-7 flex flex-wrap items-center gap-5 text-sm text-[#52606E]">
            <div className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#1F7A4B]" strokeWidth={3} /> {t("landing_v3.hero.trust_1")}</div>
            <div className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#1F7A4B]" strokeWidth={3} /> {t("landing_v3.hero.trust_2")}</div>
            <div className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#1F7A4B]" strokeWidth={3} /> {t("landing_v3.hero.trust_3")}</div>
          </div>
        </div>
        <div className="lg:col-span-6">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-[#003049]/15 aspect-[4/3]">
            <img src="/img/hero-home.jpg" alt={t("landing_v3.hero.img_alt")} className="w-full h-full object-cover" />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── WAT KRIJG JE IN DE GRATIS SCAN ─── */
function WhatYouGet() {
  const { t } = useTranslation();
  const ICONS = [TrendingDown, Banknote, Sun, HomeIcon, Map];
  const items = t("landing_v3.what.items", { returnObjects: true }) || [];
  return (
    <section id="wat-krijg-je" className="py-20 lg:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-xs uppercase tracking-[0.22em] text-[#FCBF49] font-bold mb-3">{t("landing_v3.what.eyebrow")}</div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#003049] leading-tight">{t("landing_v3.what.title")}</h2>
          <p className="mt-4 text-[#52606E] text-lg">{t("landing_v3.what.subtitle")}</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {items.map((it, i) => {
            const Icon = ICONS[i] || TrendingDown;
            return (
              <div key={i} data-testid={`v3-benefit-${i+1}`}
                className="bg-[#F7F8FA] rounded-xl p-6 hover:bg-[#FFFBEF] hover:shadow-md transition relative">
                <div className="absolute -top-3 -right-3 bg-[#FCBF49] text-[#003049] font-bold rounded-full w-7 h-7 flex items-center justify-center text-xs shadow">
                  {i + 1}
                </div>
                <Icon className="w-7 h-7 text-[#FCBF49] mb-4" />
                <h3 className="font-bold text-[#003049] mb-2 leading-tight">{it.title}</h3>
                <p className="text-sm text-[#52606E] leading-relaxed">{it.desc}</p>
              </div>
            );
          })}
        </div>
        <div className="mt-12 bg-[#FFFBEF] border border-[#FCBF49]/30 rounded-xl p-6 max-w-3xl mx-auto text-center">
          <p className="text-[#003049] leading-relaxed">
            <Sparkles className="w-4 h-4 inline-block text-[#FCBF49] mb-1 mr-1" />
            {t("landing_v3.what.note")}
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─── HOE WERKT HET ─── */
function HowItWorks() {
  const { t } = useTranslation();
  const steps = t("landing_v3.how.steps", { returnObjects: true }) || [];
  return (
    <section id="hoe-werkt-het" className="py-20 lg:py-28 bg-[#F7F8FA]">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-xs uppercase tracking-[0.22em] text-[#FCBF49] font-bold mb-3">{t("landing_v3.how.eyebrow")}</div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#003049] leading-tight">{t("landing_v3.how.title")}</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((s, i) => (
            <div key={i} data-testid={`v3-step-${i+1}`} className="text-center md:text-left">
              <div className="font-bold text-5xl text-[#FCBF49] mb-3 leading-none">{String(i + 1).padStart(2, "0")}</div>
              <h3 className="font-bold text-xl text-[#003049] mb-2">{s.t}</h3>
              <p className="text-[#52606E] leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── COMPARE OFFERTES CTA (NEW) ─── */
function CompareCTA() {
  const { t } = useTranslation();
  const bullets = t("landing_v3.compare_cta.bullets", { returnObjects: true }) || [];
  return (
    <section className="py-20 lg:py-24 bg-white" data-testid="v3-compare-cta">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="relative bg-gradient-to-br from-[#FFFBEF] via-white to-[#FFFBEF] border-2 border-[#FCBF49]/40 rounded-3xl overflow-hidden p-8 lg:p-14">
          {/* decorative blob */}
          <div className="absolute -top-16 -right-16 w-64 h-64 bg-[#FCBF49]/10 rounded-full blur-3xl" aria-hidden="true" />
          <div className="absolute -bottom-20 -left-10 w-72 h-72 bg-[#1F7A4B]/5 rounded-full blur-3xl" aria-hidden="true" />
          <div className="relative grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 bg-white border border-[#FCBF49] rounded-full px-4 py-1.5 mb-5">
                <FileText className="w-4 h-4 text-[#FCBF49]" />
                <span className="text-xs font-bold text-[#003049] uppercase tracking-wider">{t("landing_v3.compare_cta.eyebrow")}</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#003049] leading-[1.1] mb-5">
                {t("landing_v3.compare_cta.title")}
              </h2>
              <p className="text-[#52606E] text-lg leading-relaxed mb-7 max-w-xl">
                {t("landing_v3.compare_cta.subtitle")}
              </p>
              <ul className="space-y-3 mb-8">
                {bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-[#003049]">
                    <Check className="w-5 h-5 text-[#1F7A4B] shrink-0 mt-0.5" strokeWidth={3} />
                    <span className="leading-relaxed">{b}</span>
                  </li>
                ))}
              </ul>
              <Link to="/offertes-vergelijken" data-testid="v3-compare-cta-button"
                className="inline-flex items-center gap-2 bg-[#003049] hover:bg-[#001D2B] text-white font-bold rounded-md px-7 py-4 transition shadow-lg shadow-[#003049]/20">
                {t("landing_v3.compare_cta.cta")} <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            <div className="lg:col-span-5">
              {/* Stylized preview of the compare chart */}
              <div className="bg-white rounded-2xl border border-[#EFF2F5] shadow-2xl shadow-[#003049]/10 p-6 transform lg:rotate-2 hover:rotate-0 transition-transform duration-500">
                <div className="flex items-center gap-2 mb-4">
                  <BarChart3 className="w-5 h-5 text-[#FCBF49]" />
                  <div className="text-xs uppercase tracking-wider font-bold text-[#52606E]">€ / kWp</div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[{h:55,n:"A",p:"€1.450"},{h:80,n:"B",p:"€1.180"},{h:42,n:"C",p:"€1.620"}].map((b,i) => (
                    <div key={i} className="text-center">
                      <div className="h-32 flex items-end justify-center mb-2">
                        <div
                          className={`w-full rounded-t-md ${i===1?"bg-[#FCBF49]":"bg-[#003049]/15"}`}
                          style={{ height: `${b.h}%` }}
                        />
                      </div>
                      <div className="text-xs font-bold text-[#003049]">{b.n}</div>
                      <div className="text-xs text-[#52606E]">{b.p}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-[#EFF2F5] flex items-center justify-between text-xs">
                  <span className="text-[#52606E]">Best value</span>
                  <span className="inline-flex items-center gap-1 bg-[#FCBF49] text-[#003049] font-bold px-2 py-0.5 rounded-full">
                    <Star className="w-3 h-3 fill-[#003049]" /> B
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── REVIEWS ─── */
function Reviews() {
  const { t } = useTranslation();
  const reviews = t("landing_v3.reviews.list", { returnObjects: true }) || [];
  return (
    <section className="py-20 lg:py-24 bg-[#F7F8FA]">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <div className="flex justify-center items-center gap-1 mb-3">
            {[1,2,3,4,5].map(s => <Star key={s} className="w-5 h-5 text-[#FCBF49] fill-[#FCBF49]" />)}
            <span className="ml-2 font-semibold text-[#003049]">{t("landing_v3.reviews.rating")}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#003049]">{t("landing_v3.reviews.title")}</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {reviews.map((r, i) => (
            <div key={i} data-testid={`v3-review-${i+1}`} className="bg-white rounded-2xl p-7">
              <div className="flex gap-0.5 mb-4">{[1,2,3,4,5].map(s => <Star key={s} className="w-4 h-4 text-[#FCBF49] fill-[#FCBF49]" />)}</div>
              <p className="text-[#003049] leading-relaxed mb-5">&ldquo;{r.q}&rdquo;</p>
              <div className="text-sm">
                <div className="font-semibold text-[#003049]">{r.n}</div>
                <div className="text-[#52606E]">{r.c}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── ZONNECHECK FORM (gratis) ─── */
function SunCheckForm({ regions }) {
  const { t, i18n } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [region, setRegion] = useState("madrid");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const bullets = t("landing_v3.form.bullets", { returnObjects: true }) || [];

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !email || !whatsapp) { toast.error(t("landing_v3.form.error_required")); return; }
    setBusy(true);
    try {
      const utm = getUtm();
      const r = await apiClient.post("/leads", {
        name, email, whatsapp, region, language: i18n.language || "nl",
        funnel: "v3",
        ...(utm ? { utm } : {}),
      });
      setDone(r.data);
      trackLead({
        content_name: "DoctorSol Free Solar Scan",
        content_category: "lead",
      });
      toast.success(t("landing_v3.form.success_toast"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("landing_v3.form.error_default"));
    } finally { setBusy(false); }
  };

  return (
    <section id="zonnecheck" className="py-20 lg:py-28 bg-gradient-to-br from-[#003049] to-[#001D2B] text-white">
      <div className="max-w-5xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 rounded-full px-4 py-1.5 mb-5">
            <span className="text-xs font-semibold text-[#FCBF49] uppercase tracking-wider">{t("landing_v3.form.badge")}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5">
            {t("landing_v3.form.title_1")}<br />{t("landing_v3.form.title_2")}
          </h2>
          <p className="text-white/80 mb-7 text-lg leading-relaxed">{t("landing_v3.form.subtitle")}</p>
          <ul className="space-y-3">
            {bullets.map((b, i) => (
              <li key={i} className="flex items-center gap-3 text-white/90">
                <Check className="w-5 h-5 text-[#FCBF49]" strokeWidth={3} />{b}
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-6">
          <div className="bg-white rounded-2xl p-7 sm:p-9 text-[#003049] shadow-2xl">
            {!done ? (
              <form onSubmit={submit} data-testid="v3-sun-check-form" className="space-y-4">
                <FldV3 label={t("landing_v3.form.name")} value={name} onChange={setName} testid="v3-form-name" />
                <div className="grid sm:grid-cols-2 gap-4">
                  <FldV3 label={t("landing_v3.form.email")} type="email" value={email} onChange={setEmail} testid="v3-form-email" />
                  <FldV3 label={t("landing_v3.form.whatsapp")} value={whatsapp} onChange={setWhatsapp} placeholder="+34..." testid="v3-form-whatsapp" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-[#52606E] mb-1.5 font-semibold">{t("landing_v3.form.region")}</label>
                  <select value={region} onChange={(e) => setRegion(e.target.value)} data-testid="v3-form-region"
                    className="w-full bg-[#F7F8FA] border border-[#E5E9EE] rounded-md px-4 py-3 outline-none focus:border-[#FCBF49] focus:ring-2 focus:ring-[#FCBF49]/30">
                    {regions.length === 0 ? <option value="madrid">Madrid</option> : regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
                <button type="submit" disabled={busy} data-testid="v3-form-submit"
                  className="w-full bg-[#FCBF49] hover:bg-[#E5AB2F] text-[#003049] rounded-md px-7 py-4 font-bold flex items-center justify-center gap-2 transition disabled:opacity-60">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t("landing_v3.form.submit")} <ArrowRight className="w-4 h-4" /></>}
                </button>
                <p className="text-xs text-[#52606E] text-center pt-1">{t("landing_v3.form.disclaimer")}</p>
              </form>
            ) : (
              <div className="text-center py-5" data-testid="v3-form-success">
                <div className="w-16 h-16 rounded-full bg-[#FCBF49]/20 mx-auto mb-5 flex items-center justify-center">
                  <Sparkles className="w-8 h-8 text-[#FCBF49]" />
                </div>
                <h3 className="text-2xl font-bold mb-3">{t("landing_v3.form.success_title")}</h3>
                <p className="text-[#52606E] mb-5">{t("landing_v3.form.success_body")}</p>
                <Link to={`/scan-intake?token=${done.magic_token}`}
                  className="inline-flex items-center gap-2 bg-[#FCBF49] hover:bg-[#E5AB2F] text-[#003049] font-bold rounded-md px-6 py-3">
                  {t("landing_v3.form.success_cta")} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function FldV3({ label, value, onChange, suffix, testid, ...rest }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-[#52606E] mb-1.5 font-semibold">{label}</label>
      <div className="relative">
        <input {...rest} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
          className="w-full bg-[#F7F8FA] border border-[#E5E9EE] rounded-md px-4 py-3 outline-none focus:border-[#FCBF49] focus:ring-2 focus:ring-[#FCBF49]/30" />
        {suffix && <span className="absolute right-4 top-3 text-sm text-[#52606E]">{suffix}</span>}
      </div>
    </div>
  );
}

/* ─── MINI FAQ ─── */
function FaqMini() {
  const { t } = useTranslation();
  const qa = t("landing_v3.faq.qa", { returnObjects: true }) || [];
  return (
    <section className="py-20 lg:py-24 bg-white">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-10">
          <div className="text-xs uppercase tracking-[0.22em] text-[#FCBF49] font-bold mb-3">{t("landing_v3.faq.eyebrow")}</div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#003049]">{t("landing_v3.faq.title")}</h2>
        </div>
        <div className="space-y-3">
          {qa.map((x, i) => <FaqRow key={i} idx={i} q={x.q} a={x.a} />)}
        </div>
      </div>
    </section>
  );
}

function FaqRow({ idx, q, a }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div data-testid={`v3-faq-${idx}`} className={`border rounded-xl transition ${open ? "border-[#FCBF49] bg-[#FFFBEF]" : "border-[#EFF2F5] bg-white"}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex justify-between items-center text-left px-6 py-5">
        <span className="font-semibold text-[#003049] pr-4">{q}</span>
        <ChevronDown className={`w-5 h-5 text-[#52606E] shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-6 pb-5 text-[#52606E] leading-relaxed">{a}</div>}
    </div>
  );
}

/* ─── FOOTER ─── */
function FooterV3() {
  const { t } = useTranslation();
  return (
    <footer className="bg-[#003049] text-white pt-14 pb-8">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-3 gap-10 pb-10 border-b border-white/10">
          <div>
            <div className="flex items-center mb-4">
              <img src="/img/doctorsol-logo.svg" alt="DoctorSol" className="h-14 w-auto" style={{ filter: "brightness(0) invert(1)" }} draggable={false} />
            </div>
            <p className="text-white/70 leading-relaxed max-w-sm">{t("landing_v3.footer.tagline")}</p>
          </div>
          <div className="text-sm text-white/75 space-y-1.5">
            <div className="text-xs uppercase tracking-[0.18em] text-[#FCBF49] font-bold mb-3">{t("landing_v3.footer.contact_label")}</div>
            <div>📞 +34 621 416 789</div>
            <div>✉ info@doctorsol.es</div>
            <div>📍 Costa del Sol, España</div>
            <div className="flex gap-3 pt-3">
              <a href="https://www.facebook.com/DoctorSolEU" target="_blank" rel="noopener noreferrer" aria-label="Facebook DoctorSol"
                 className="w-9 h-9 rounded-lg bg-white/10 hover:bg-[#FCBF49] hover:text-[#003049] flex items-center justify-center transition" data-testid="social-facebook">
                <Facebook className="w-4 h-4" />
              </a>
              <a href="https://www.instagram.com/doctorsoleu/" target="_blank" rel="noopener noreferrer" aria-label="Instagram DoctorSol"
                 className="w-9 h-9 rounded-lg bg-white/10 hover:bg-[#FCBF49] hover:text-[#003049] flex items-center justify-center transition" data-testid="social-instagram">
                <Instagram className="w-4 h-4" />
              </a>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#FCBF49] font-bold mb-3">{t("landing_v3.footer.ready_label")}</div>
            <p className="text-sm text-white/75 mb-4">{t("landing_v3.footer.ready_body")}</p>
            <a href="#zonnecheck" className="inline-flex items-center gap-2 bg-[#FCBF49] hover:bg-[#E5AB2F] text-[#003049] font-bold rounded-md px-6 py-3 text-sm">
              {t("landing_v3.footer.ready_cta")} <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
        <div className="pt-6 flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-white/50">
          <div>{t("landing_v3.footer.copyright")}</div>
          <div className="flex gap-5">
            <a href="/privacy" className="hover:text-white">{t("landing_v3.footer.privacy")}</a>
            <a href="/terms" className="hover:text-white">{t("landing_v3.footer.terms")}</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
